import { useState } from "react";
import { Link } from "react-router-dom";
import type { ReactNode } from "react";

export interface HeroSectionProps {
  title: string;
  subtitle: string;
  cta?: {
    label: string;
    href: string;
  };
  children?: ReactNode;
}

export function HeroSection({
  title,
  subtitle,
  cta,
  children,
}: HeroSectionProps) {
  return (
    <section className="hero-section">
      <div className="hero-content">
        <h1 className="hero-title">{title}</h1>
        <p className="hero-subtitle">{subtitle}</p>
        {children}
        {cta && (
          <Link to={cta.href} className="btn btn--primary btn--large">
            {cta.label}
          </Link>
        )}
      </div>
    </section>
  );
}

export interface FeatureCardProps {
  icon?: ReactNode;
  title: string;
  description: string;
}

export function FeatureCard({ icon, title, description }: FeatureCardProps) {
  return (
    <div className="feature-card">
      {icon && (
        <div className="feature-icon" aria-hidden="true">
          {icon}
        </div>
      )}
      <h3 className="feature-title">{title}</h3>
      <p className="feature-description">{description}</p>
    </div>
  );
}

export interface PlanCardProps {
  name: string;
  description: string;
  price?: number;
  priceUnit?: string;
  features: string[];
  cta: {
    label: string;
    href: string;
  };
  highlighted?: boolean;
}

export function PlanCard({
  name,
  description,
  price,
  priceUnit,
  features,
  cta,
  highlighted,
}: PlanCardProps) {
  return (
    <div className={`plan-card ${highlighted ? "plan-card--highlighted" : ""}`}>
      <h3 className="plan-name">{name}</h3>
      <p className="plan-description">{description}</p>
      {price !== undefined && (
        <div className="plan-price">
          <span className="plan-price-value">${price}</span>
          {priceUnit && <span className="plan-price-unit">{priceUnit}</span>}
        </div>
      )}
      <ul className="plan-features">
        {features.map((feature, idx) => (
          <li key={idx} className="plan-feature">
            <span className="plan-feature-check">✓</span>
            {feature}
          </li>
        ))}
      </ul>
      <Link to={cta.href} className="btn btn--secondary">
        {cta.label}
      </Link>
    </div>
  );
}

export interface FAQItemProps {
  question: string;
  answer: string | ReactNode;
}

export function FAQItem({ question, answer }: FAQItemProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="faq-item">
      <button
        className="faq-question"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span>{question}</span>
        <span className="faq-icon">{open ? "−" : "+"}</span>
      </button>
      {open && <div className="faq-answer">{answer}</div>}
    </div>
  );
}

export function CTASection({
  title,
  description,
  primaryCTA,
  secondaryCTA,
}: {
  title: string;
  description: string;
  primaryCTA: { label: string; href: string };
  secondaryCTA?: { label: string; href: string };
}) {
  return (
    <section className="cta-section">
      <div className="cta-content">
        <h2>{title}</h2>
        <p>{description}</p>
        <div className="cta-buttons">
          <Link to={primaryCTA.href} className="btn btn--primary">
            {primaryCTA.label}
          </Link>
          {secondaryCTA && (
            <Link to={secondaryCTA.href} className="btn btn--secondary">
              {secondaryCTA.label}
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}

export function TrustCallout({ children }: { children: ReactNode }) {
  return <div className="trust-callout">{children}</div>;
}
